import React, { useState, useEffect, useCallback } from 'react'
import { OneTable as SLTable, OneButton } from '@yy/one-ui'
import { Empty } from '@/components'
import TableEmpty from '@/bizComponents/TableEmpty'
import { useDispatch } from '@/store'
import { useTranslation } from 'react-i18next'
import { setHideSelectAllTips } from '@/store/commonSlice'
import goodsEmpty from '@/static/imgs/table-empty.png'
import type { IOneTableProps } from '@yy/one-ui/es/OneTable'
import { PAGINATION_CONFIG } from '@/constants/tableConfig'
import styles from './index.module.less'
import { useSelector } from '@/store'
import classNames from 'classnames'
import { ResizeObserver } from '@juggle/resize-observer'

interface OneTableProps extends IOneTableProps {
  /**是否需要空状态 */
  needEmptyState?: boolean
  /**空状态 */
  empty?: boolean
  /**空状态标题 */
  emptyTitle?: string
  /**空状态描述 */
  emptyDesc?: string
  /**空状态占位 */
  emptyExtra?: React.ReactNode
  /** 空态图url */
  emptyImgUrl?: string
}

const OneTable = (props: OneTableProps) => {
  const { pagination, needEmptyState = true, empty, emptyTitle, emptyDesc, emptyExtra, rowSelection, emptyImgUrl = goodsEmpty, ...rest } = props
  const { t } = useTranslation()
  const [position, setPosition] = useState({ left: 0, top: 0 })
  const hideSelectAllTips = useSelector((state) => state.common.hideSelectAllTips)
  const dispatch = useDispatch()

  /**
   * @description: 隐藏全选提示
   * @param {*} useCallback
   * @author: cd
   * @Date: 2022-03-30 17:16:12
   */
  const hideTipsHandler = useCallback(() => {
    dispatch(setHideSelectAllTips(true))
  }, [dispatch])

  /**
   * @description: 提示定位 & 监听全选关闭提示
   * @param {*}
   * @author: cd
   * @Date: 2022-03-30 17:16:32
   */
  useEffect(() => {
    let ro
    if (!!rowSelection && !hideSelectAllTips) {
      const selectableBox = document.querySelector('.selectableBox')
      const selectable = document.querySelector('.ant-table-thead').querySelector('.ant-table-selection-column')
      const SelectAllHandler = rowSelection.onSelectAll
      rowSelection.onSelectAll = (selected, selectedRows, changeRows) => {
        SelectAllHandler?.(selected, selectedRows, changeRows)
        hideTipsHandler()
      }
      ro = new ResizeObserver(() => {
        const rect = selectable?.getBoundingClientRect()
        const parentRect = selectableBox?.getBoundingClientRect()
        setPosition({
          left: rect?.width / 2 - 95,
          top: rect?.height / 2 + 16 + rect?.top - parentRect?.top,
        })
      })
      ro.observe(document.body)
    }
    return () => {
      ro?.unobserve(document.body)
    }
  }, [hideSelectAllTips, rowSelection, hideTipsHandler])

  return (
    <>
      {!needEmptyState || !empty ? (
        <div
          className={classNames(styles.tableBox, {
            selectableBox: !!rowSelection,
          })}
        >
          <SLTable
            pagination={
              pagination
                ? {
                    showSizeChanger: true,
                    pageSizeOptions: PAGINATION_CONFIG.pageSizeOptions,
                    ...pagination,
                    showTotal: (total) => `${t('common.pagination.showTotal', { total: total })}`,
                    locale: {
                      items_per_page: t('pagination.items_per_page'),
                    },
                  }
                : false
            }
            locale={{
              emptyText: <Empty text={t('order.index.noFound')}></Empty>,
            }}
            rowSelection={rowSelection}
            scroll={{ x: 1000 }}
            {...rest}
          ></SLTable>
          {!!rowSelection && !hideSelectAllTips && (
            <div className={styles.selectAllTips} style={{ ...position }}>
              👆👆{t('common.table.selectTips')}
              <OneButton className={styles.btn} type="primary" onClick={hideTipsHandler}>
                {t('store.auth.bind.storeLimit.know')}
              </OneButton>
            </div>
          )}
        </div>
      ) : (
        <TableEmpty src={emptyImgUrl}>
          {emptyTitle && <p style={{ color: '#242833', fontSize: '16px' }}>{emptyTitle}</p>}
          {emptyDesc && <p style={{ color: '#666666' }}>{emptyDesc}</p>}
          {emptyExtra}
        </TableEmpty>
      )}
    </>
  )
}

export default OneTable
